import React, { useState, useMemo } from 'react';

import styled from 'styled-components';

import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import { NwButton } from '@components/Gui/NwButton';
import SendAgendaDrawerMobile from '@components/SendAgendaDrawer/SendAgendaDrawerMobile';

import { useAgendaContext } from './AgendaContext';
import MobileAgendaDatePicker from './MobileAgendaDatePicker';
import MobileAgendaEvent from './MobileAgendaEvent';
import MobileAgendaHeader from './MobileAgendaHeader';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const MobileAgendaWrapper = styled.div`
    .mobile-agenda-datepicker-wrapper {
        margin: 0.5rem 0;
        padding: 0.5rem 1rem;
        background: white;
    }

    .mobile-agenda-header-and-events {
        .empty-state-container {
            margin-top: 2rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
        }
    }

    .mobile-agenda-send-button {
        display: flex;
        justify-content: flex-end;
        padding: 0.25rem 1rem;
        background: white;
    }

    .castings-loading-container {
        width: 100%;
        background: ${(props) => props.theme.PrimaryColor};
        padding: 8px;
        text-align: center;
        color: white;
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;

        .loading-label {
            margin-left: 8px;
        }
    }
`;

const MobileAgendaContainer = () => {
    const { eventsToDisplay, isLoadingDay, isLoadingMonth, isMobileDrawer, contactId, previewMode, scheduleRange, type } = useAgendaContext();
    const [sendAgenda, setSendAgenda] = useState(false);

    // Event type filters - job, casting and extjob active by default
    const [activeFilters, setActiveFilters] = useState(['job', 'casting', 'ext', 'extjob']);

    // Map filter categories to TypeName values
    const filterTypeMap = {
        job: ['Job'],
        ext: ['Ext'],
        casting: ['Casting'],
        activities: ['FollowUp'],
        travel: ['Travel'],
        accommodation: ['Accommodation'],
        extjob: ['ExtJob'],
        other: ['Meeting', 'Notice'],
    };

    // Filter events based on active filters - only if contactId is null
    const filteredEvents = useMemo(() => {
        if (!eventsToDisplay) {
            return [];
        }

        // If contactId is set, show all events without filtering
        if (contactId) {
            return eventsToDisplay;
        }

        // Apply filters only when contactId is null
        if (activeFilters.length === 0) {
            return [];
        }

        // If all filters are active, return all events
        if (activeFilters.length === 7) {
            return eventsToDisplay;
        }

        // Get all allowed TypeNames from active filters
        const allowedTypes = activeFilters.flatMap((filter) => filterTypeMap[filter] || []);

        return eventsToDisplay.filter((event) => allowedTypes.includes(event.TypeName));
    }, [eventsToDisplay, activeFilters, contactId]);

    const toggleFilter = (filterName) => {
        setActiveFilters((prev) => {
            if (prev.includes(filterName)) {
                return prev.filter((f) => f !== filterName);
            } else {
                return [...prev, filterName];
            }
        });
    };

    return isLoadingMonth ? (
        <Loading textBlack small paddingTop="3rem" />
    ) : (
        <>
        <MobileAgendaWrapper $isMobileDrawer={isMobileDrawer}>
            <div className="mobile-agenda-datepicker-wrapper">
                <MobileAgendaDatePicker />
            </div>
            <div className="mobile-agenda-header-and-events">
                <MobileAgendaHeader activeFilters={contactId ? [] : activeFilters} onToggleFilter={toggleFilter} />
                {!previewMode && type === 'model' && !isLoadingDay && filteredEvents && filteredEvents.length > 0 && (
                    <div className="mobile-agenda-send-button">
                        <NwButton
                            ghost
                            icon={byPrefixAndName.fasl['share']}
                            size="small"
                            label="send agenda"
                            onClick={() => setSendAgenda(true)}
                        />
                    </div>
                )}
                {isLoadingDay ? (
                    <Loading textBlack small paddingTop="3rem" />
                ) : filteredEvents && filteredEvents.length ? (
                    <div className="mobile-agenda-events">
                        {filteredEvents.map((ev, index) => (
                            <MobileAgendaEvent key={`${ev.TypeName}-${ev.ID}-${index}`} event={ev} />
                        ))}
                    </div>
                ) : (
                    <div className="empty-state-container">
                        <EmptyState message="no events" />
                    </div>
                )}
            </div>
        </MobileAgendaWrapper>
        {sendAgenda && (
            <SendAgendaDrawerMobile
                contactID={contactId}
                scheduleRange={scheduleRange}
                onClose={() => setSendAgenda(false)}
            />
        )}
        </>
    );
};

export default MobileAgendaContainer;
